import React, { useEffect, useMemo, useState } from "react";
import { Button, FormControl, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Block from "../Block";
import Icon from "../Icon";
import Title from "../Title";
import {
  useGetOauthApplicationsQuery,
  useCreateOauthApplicationMutation,
  useCreateOauthAuthorizationMutation,
} from "../../api/oauthApiSlice";
import DocFr from "./DocFr";
import DocEn from "./DocEn";
import ShowItem from "../ShowItem";
import { toast } from "react-toastify";
import { Loader } from "../components";

const ApplicationDescription = ({ application }) => {
  const { t } = useTranslation();
  const [authorize, {data: authorizationData={}, isLoading, isSuccess, isError, error}] = useCreateOauthAuthorizationMutation();
  const code = useMemo(() => ( authorizationData.code ? authorizationData.code : "[CODE]" ), [authorizationData]);
  const curl_cmd = `curl -X POST -H "Content-Type: application/x-www-form-urlencoded" -d "grant_type=authorization_code&code=${code}&redirect_uri=${application.redirect_uri}&client_id=${application.uid}&client_secret=${application.secret}\" ${application.oauth_token_url}`;
  const authorizationParams = (application) => {
    const uri = application.redirect_uri.split(" ")[0];
    return {
      client_id: application.uid,
      redirect_uri: uri,
      response_type: "code",
      scope: application.scopes,
    };
  };
  const loaderProps = { success: (!isLoading), error: (!isLoading && isError) }

  useEffect(() => {
    if(isError && error) {
      toast.error(t("oauth.error", { error: error.data.message }));
    }
  }, [isError, error]);
  return (
    <Block title={t("oauth.credentials")}>
      <Loader {...loaderProps}>
        <InputGroup className="mb-3">
          <FormControl
            disabled
            value={authorizationData.code}
            placeholder={t("oauth.placeholder_code")}
            aria-label={t("oauth.code")}
          />
          <InputGroup.Append>
            <Button
              onClick={() =>
                authorize(authorizationParams(application))
              }
            >
              {t("oauth.authorize")}
            </Button>
          </InputGroup.Append>
        </InputGroup>
        <ShowItem label={t("oauth.application_uid")} value={application.uid} />
        <ShowItem label={t("oauth.secret")} value={application.secret} />
        <ShowItem label={t("oauth.curl_command")} valueStyle={ { overflowWrap: 'break-word' } } value={curl_cmd} />
      </Loader>
    </Block>
  );
};

const OauthNoPublisherApplication = () => {
  const { t } = useTranslation();
  const [
    createApplication,
    { data: applicatons,
    isLoading,
    isSuccess,
    isError,
    refetch,
    error }
  ] = useCreateOauthApplicationMutation();

  useEffect(() => {
    if(isError && error) {
      toast.error(t("oauth.error", { error: error.data.message }));
    }
  }, [isError, error]);

  return (<>
    <Block title={t("oauth.credentials")}>
      <p>
        {t("oauth.no_application")}
      </p>
      <Button
        onClick={() => {
          createApplication();
        }}
      >
        <Icon icon={"plus"} />
        {t("oauth.add")}
      </Button>
    </Block>
  </>)
}

const OauthPublisherApplication = () => {
  const {
    data: applications,
    isLoading,
    isSuccess,
    isError,
    refetch,
    error
  } = useGetOauthApplicationsQuery();
  const { t, i18n } = useTranslation();
  const loaderProps = { success: (!isLoading && isSuccess), error: (!isLoading && isError), retry: refetch }

  useEffect(() => {
    if(isError && error) {
      toast.error(t("oauth.error", { error: error.data.message }));
    }
  }, [isError, error]);

  const [hasApplications, setHasApplications] =  useState(false);
  useEffect(() => {
    if (applications && applications.applications) {
      setHasApplications(applications.applications.length > 0);
    }
  }, [JSON.stringify(applications)]);

  return (
    <>
      <Title main={t("oauth.title")} />
      <Loader {...loaderProps}>
        { !hasApplications && <OauthNoPublisherApplication />}
        { hasApplications && (
          applications &&
            applications.applications.map((application) => (
              <ApplicationDescription
                key={application.id}
                application={application}
              />
            ))
          ) }
      </Loader>
    
      <Block title={t("oauth.documentation")}>
        {i18n.language === "fr" ? <DocFr /> : <DocEn />}
      </Block>
    </>
  );
};

export default OauthPublisherApplication;
